// medplum-previewboot.js — ONLINE-PRIMARY (main-2) replacement for the LAST leg
// of the deleted Express GET /view/bootstrap.js: the routes that reached the
// server "fallback" bootstrap after every converted staff route (MEDPLUM_LOADERS)
// and every magic-link anon loader (medplum-intake/medplum-portal) had already
// claimed theirs. A census of those remaining legs (see bridge.js) showed NONE
// still needs a server payload:
//
//   • token-display (/token-display) — the screen SELF-LOADS the same-origin,
//     no-PHI public board projection and IGNORES window.__OPD_DATA__. It only needs a
//     bridge OBJECT to exist so ScreenBoot can mount it.
//   • token-LESS intake / portal / anon-encounter — /intake·/intake/step·
//     /intake/review·/intake/photos·/saathi·/portal·/summary·/tablet-intake/:id·
//     /saathi/:id opened with NO magic-link {token}. This is the demo/PREVIEW
//     affordance: with no token there is no patient to read, so the screens render
//     their OWN demo fallbacks off installBridge()'s `pick` (which returns the
//     inline demo whenever the payload isn't "live" for that root). A real
//     magic-link WITH a token never reaches here — bridge.js's anonLoaderFor()
//     routes it to the RLS-native chart-read Bot loaders above this leg.
//   • the legacy 'admin' bootstrap — GONE: every admin screen now boots 'settings'
//     Medplum-direct (grep App.jsx `bootstrap:` → no 'admin').
//
// So this builds a minimal, META-ONLY __OPD_DATA__ CLIENT-SIDE — no Express, no
// auth, no anon client login (keeping the preview dependency-free, more robust than
// the old server round-trip which could 401/500). Deliberately absent: the
// intake/portal/encounter/queue/reception ROOT keys — their absence is what makes
// `pick` serve the demo fallback and `isLive()` return false, reproducing the exact
// token-less preview render.
//
// PARITY NOTE (deliberate): the old token-less Express payload also carried the
// REAL hospital branding block (getHospitalIdentity()). The anon-safe source for
// that (cfg.hospital) is only reachable via an anon ClientApplication login, which
// this no-auth preview should not depend on — an unprovisioned anon client would
// then break a preview the old path served credential-free. So the preview shows
// the bridge's DEMO branding instead; a real patient (token present) always gets
// real branding via the Medplum loaders. Logged as the one parity difference.

const LANG_KEY = 'healther.opd.lang';

function readLang(lang) {
  if (lang) return lang;
  try { return localStorage.getItem(LANG_KEY) || 'en'; } catch (_) { return 'en'; }
}

/**
 * Build the meta-only preview/self-load bootstrap payload for the routes that used
 * to hit the Express /view/bootstrap.js fallback. Pure (no I/O) so it is trivially
 * unit-testable and can never throw a network error into ScreenBoot.
 *
 * The shape mirrors the anon loaders' envelope MINUS any chart root:
 *   { meta:{ route,id,token,lang,preview,now }, currentUser:null, features:{rppg} }
 * — currentUser:null keeps ScreenBoot's staff gate honest (all these routes are
 * staff:false), and features.rppg:false matches the server default the intake
 * screens read (window.__OPD_DATA__.features.rppg).
 *
 * @param {string} route  the SCREENS[].bootstrap value ('token-display'|'intake'|
 *                         'portal'|'encounter')
 * @param {object} [opts] { id, token, lang } passed through from ScreenBoot
 * @returns {object} the window.__OPD_DATA__ payload (NO live root keys)
 */
export function buildPreviewBoot(route, { id = '', token = '', lang = '' } = {}) {
  return {
    meta: {
      route: route || '',
      id: id || '',
      token: token || '',
      lang: readLang(lang),
      preview: true,
      now: new Date().toISOString(),
    },
    currentUser: null,
    features: { rppg: false },
  };
}
